import { Activity } from '@shared/models/design/activities/activity.model';
import { ActivityReference, Storey } from '@shared/models/design/storey.model';
import { guards } from '../typeguards.helpers';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { designGuards } from './design-typeguards.helpers';
import { getId, replaceProperties } from '../object.helpers';
import { compare } from '../compare.helpers';
import { activityRefGuards } from './guards/activity-guards.helpers';
import {
  ActivityMix,
  ActivityType,
} from '@shared/models/design/activities/activity-type.model';
import { sumProperties } from '../math.helpers';
import { omitCalculatedAreaActivitiesFromActivityMix } from './activity-mix.helpers';

const SHARE_EPSILON = 1e-9;

export type ResolveSharesOptions = {
  preserveRefId?: ActivityReference['id'];
};

/**
 * Creates an updated activity list with new shares.
 * @param activities Activities in the {@link Storey}.
 * @param sharesById New shares per {@link ActivityReference} id.
 * @returns A new list with updated shares.
 */
const applyActivityRefShares = (
  activities: Storey['activities'],
  sharesById: Map<ActivityReference['id'], number>,
): Storey['activities'] => {
  if (sharesById.size === 0) return activities;
  return activities.map((ref) => {
    if (activityRefGuards.is.calculatedAreaActivityRef(ref)) return ref;
    const share = sharesById.get(ref.id);
    if (share === undefined) return ref;
    return { ...ref, share };
  });
};

/**
 * Redistributes shares for activity references in a {@link Storey}.
 * Ensures the sum is 1 and preserves ignored shares.
 * @param storey {@link Storey} containing activity references.
 * @param ignoreIds Ids that should not be redistributed.
 * @returns A new {@link Storey} with redistributed shares.
 */
export const redistributeActivityRefsShares = (
  storey: Storey,
  ...ignoreIds: ActivityReference['id'][]
): Storey => {
  const ignoreSet = new Set(ignoreIds);
  const shareableRefs = storey.activities.filter(
    (ref) => !activityRefGuards.is.calculatedAreaActivityRef(ref),
  );

  // If only one reference exists, always set share to 1
  if (shareableRefs.length === 1) {
    const onlyRef = shareableRefs[0];
    if (onlyRef && onlyRef.share !== 1) {
      const sharesById = new Map<ActivityReference['id'], number>([
        [onlyRef.id, 1],
      ]);
      return {
        ...storey,
        activities: applyActivityRefShares(storey.activities, sharesById),
      };
    }
    return storey;
  }

  const ignoredRefs = shareableRefs.filter((ref) => ignoreSet.has(ref.id));
  const redistributableRefs = shareableRefs.filter(
    (ref) => !ignoreSet.has(ref.id),
  );

  const ignoredTotal = ignoredRefs.reduce(
    (sum, ref) => sum + (ref.share ?? 0),
    0,
  );

  // Scale down ignored shares if they exceed 1
  const ignoredScale =
    ignoredTotal > 1 && ignoredRefs.length > 0 ? 1 / ignoredTotal : 1;
  const finalIgnoredTotal = ignoredTotal * ignoredScale;
  const remainingShare = Math.max(0, 1 - finalIgnoredTotal);

  if (redistributableRefs.length === 0) {
    if (ignoredScale === 1) return storey;
    const sharesById = new Map<ActivityReference['id'], number>();
    ignoredRefs.forEach((ref) => {
      sharesById.set(ref.id, (ref.share ?? 0) * ignoredScale);
    });
    return replaceProperties(storey, {
      activities: applyActivityRefShares(storey.activities, sharesById),
    });
  }

  const redistributableTotal = redistributableRefs.reduce(
    (sum, ref) => sum + (ref.share ?? 0),
    0,
  );

  const sharesById = new Map<ActivityReference['id'], number>();
  if (ignoredScale !== 1) {
    ignoredRefs.forEach((ref) => {
      sharesById.set(ref.id, (ref.share ?? 0) * ignoredScale);
    });
  }

  if (redistributableTotal <= 0) {
    // Distribute equally when shares are missing
    const equalShare = remainingShare / redistributableRefs.length;
    redistributableRefs.forEach((ref) => {
      sharesById.set(ref.id, equalShare);
    });
  } else {
    // Proportional redistribution based on current shares
    const scale = remainingShare / redistributableTotal;
    redistributableRefs.forEach((ref) => {
      sharesById.set(ref.id, (ref.share ?? 0) * scale);
    });
  }

  return replaceProperties(storey, {
    activities: applyActivityRefShares(storey.activities, sharesById),
  });
};

export const sumActivityShares = (references: ActivityReference[]): number => {
  return references.reduce((total, ref) => total + (ref.share ?? 0), 0);
};

export const areActivityRefsEqual = (
  a: ActivityReference[],
  b: ActivityReference[],
): boolean => {
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    const left = a[index];
    const right = b[index];
    if (!left || !right) return false;
    if (left.id !== right.id) return false;
    if (left.activity_id !== right.activity_id) return false;
    if (left.type !== right.type) return false;
    const leftShare = left.share ?? 0;
    const rightShare = right.share ?? 0;
    if (Math.abs(leftShare - rightShare) > SHARE_EPSILON) return false;
  }
  return true;
};

/**
 * Get the activities that are not assigned to the storey
 * @param storey The storey to get the available activities for
 * @param activities The activities to get the available activities from
 * @returns The available activities
 */
export const getAvailableActivities = (
  storey: Storey,
  activities: Activity[],
): Activity[] => {
  const assigned = new Set(storey.activities.map((ref) => ref.activity_id));
  return activities
    .filter(({ type }) => activityRefGuards.is.programActivityType(type))
    .filter((activity) => !assigned.has(activity.id));
};

export const clampActivityRefShare = (value: number): number => {
  if (!guards.is.finiteNumber(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
};

export type ActivityRefSelector =
  ItemOrItemId<ActivityReference> | ActivityReference['type'];

/**
 * Get an activity reference from a storey by selector
 * @param storey
 * @param selector
 * @returns
 */
export const getActivityReference = (
  storey: Storey,
  selector: ActivityRefSelector,
): ActivityReference | undefined => {
  if (designGuards.is.activityType(selector)) {
    return storey.activities.find((ref) => ref.type === selector);
  }

  const id = getId(selector);
  return storey.activities.find((ref) => ref.id === id);
};

/**
 * Sort activity references by type. Calculated activity references are sorted last.
 * @param refs
 * @returns A new array with the activity references sorted by type. If the sorted refs are the same as the original refs, return the original refs to avoid unnecessary re-renders.
 */
export const sortActivityRefsByType = (
  refs: ActivityReference[],
): ActivityReference[] => {
  const sortedRefs = refs.slice().sort((a, b) => {
    const aSort = activityRefGuards.is.calculatedAreaActivityRef(a) ? 1 : -1;
    const bSort = activityRefGuards.is.calculatedAreaActivityRef(b) ? 1 : -1;
    return bSort - aSort;
  });

  // If the sorted refs are the same as the original refs, return the original refs
  return compare.equal.shallow(sortedRefs, refs) ? refs : sortedRefs;
};

/**
 * Calculate resolved share factors per {@link ActivityReference} for a storey.
 * NOTE: Only use this while the activity mix is not yet updated (during edit)
 * @param localRefs Current modified refs
 * @param storeyGfa
 * @param activityMix
 * @returns Record keyed by activity ref id with resolved share factors
 */
export const getRefShareRecord = (
  localRefs: ActivityReference[],
  activityMix: ActivityMix,
): Record<ActivityReference['id'] | ActivityType.Unset, number> => {
  const total = sumProperties(activityMix);

  // Area that is shared by any refs with "share" (non-calculated area activities)
  const shareArea = sumProperties(
    omitCalculatedAreaActivitiesFromActivityMix(activityMix),
  );
  const shareAreaRatio = shareArea / total;

  const record = localRefs.reduce(
    (acc, ref) => {
      if (!total) {
        acc[ref.type] = 0;
      } else if (activityRefGuards.is.calculatedAreaActivityRef(ref)) {
        acc[ref.type] = (activityMix[ref.type] ?? 0) / total;
      } else {
        acc[ref.type] = (ref.share ?? 0) * shareAreaRatio;
      }
      return acc;
    },
    {} as Record<ActivityReference['id'] | ActivityType.Unset, number>,
  );
  const recordTotal = sumProperties(record);

  // If we're not using all the area, add the remaining area to the unset activity
  if (recordTotal < 1) {
    record[ActivityType.Unset] = Math.min(1, 1 - recordTotal);
  }
  return record;
};

/**
 * Creates a synthetic unset activity reference for a storey.
 * @param storeyId Storey id to scope the reference id
 * @returns {@link ActivityReference} for unset area
 */
export const createUnsetActivityReference = (
  storeyId: Storey['id'],
): ActivityReference => {
  return {
    id: `unset-${storeyId}`,
    type: ActivityType.Unset,
    activity_id: ActivityType.Unset,
  };
};
